import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { messages } from '../../../messages';
import { JiraScope, ISSUE_LIST_LIMIT, OTHERS_LIST_LIMIT_NO_ADVANCED_ROADMAPS_ACCESS, OTHERS_LIST_LIMIT_ADVANCED_ROADMAPS_ACCESS } from './types';
import { useQueryParams } from './jira-analytics-query-params';
import { AttributeType } from '../../../jira/clients/response-types';
import { defaultResultRenderer } from '../../../extensible/search-result/default-result-renderer';
import { HoverCardWrapper } from './sections.styled';
import { getTimeDetailForJiraIssueResult } from './utils';
import { sectionTitleRenderer } from './title-renderer';
import ErrorBoundary from '../../../error-boundary';
const HoverCard = /*#__PURE__*/React.lazy(() => import( /* webpackChunkName: "@atlaskit-internal_smart-card/hover-card" */'@atlaskit/smart-card/hover-card').then(mod => ({
  default: mod.HoverCard
})));
const HoverCardResultWrapper = ({
  children,
  url = ''
}) => {
  return /*#__PURE__*/React.createElement(HoverCardWrapper, null, /*#__PURE__*/React.createElement(ErrorBoundary, {
    fallback: children
  }, /*#__PURE__*/React.createElement(React.Suspense, {
    fallback: children
  }, /*#__PURE__*/React.createElement(HoverCard, {
    url: url,
    hidePreviewButton: true
  }, children))));
};
const viewAllLinkRendererIssues = ({
  section,
  totalResults
}) => {
  var _section$searchResult;
  const isTotalItemsGreaterThanTheLimit = (totalResults || ((_section$searchResult = section.searchResults) === null || _section$searchResult === void 0 ? void 0 : _section$searchResult.length)) > (section.resultLimit || 0);
  return isTotalItemsGreaterThanTheLimit ? /*#__PURE__*/React.createElement(FormattedMessage, messages.common_show_all_results_link) : /*#__PURE__*/React.createElement(React.Fragment, null);
};
export const useSections = (hasAdvancedRoadmapsAccess, hasSoftwareAccess, viewAllLinkGenerator, isPreviewOnJiraSearchResultsEnabled, intl, formatDate, isConsistentIssueDetailsEnabled, autocorrectEnabled) => {
  const appendQueryParams = useQueryParams();
  const mapJiraItems = response => response.results.map(item => {
    var _item$attributes, _item$attributes$avat;
    const base = {
      id: item.id,
      title: item.name,
      url: appendQueryParams(item.url || '', item),
      meta: '',
      iconUrl: ((_item$attributes = item.attributes) === null || _item$attributes === void 0 ? void 0 : (_item$attributes$avat = _item$attributes.avatar) === null || _item$attributes$avat === void 0 ? void 0 : _item$attributes$avat.url) || '',
      type: item.attributes['@type']
    };
    if (item.attributes && item.attributes['@type'] === AttributeType.issue) {
      var _item$attributes2, _item$attributes3, _item$attributes3$con, _item$attributes4, _item$attributes5;
      return {
        ...base,
        title: `${(_item$attributes2 = item.attributes) === null || _item$attributes2 === void 0 ? void 0 : _item$attributes2.key}  ${item.name}`,
        meta: ((_item$attributes3 = item.attributes) === null || _item$attributes3 === void 0 ? void 0 : (_item$attributes3$con = _item$attributes3.container) === null || _item$attributes3$con === void 0 ? void 0 : _item$attributes3$con.title) || '',
        containerId: (_item$attributes4 = item.attributes) === null || _item$attributes4 === void 0 ? void 0 : _item$attributes4.containerId,
        additionalMeta: getTimeDetailForJiraIssueResult(intl, formatDate, (_item$attributes5 = item.attributes) === null || _item$attributes5 === void 0 ? void 0 : _item$attributes5.updated)
      };
    }
    if (item.attributes && item.attributes['@type'] === AttributeType.board) {
      var _item$attributes6, _item$attributes6$con, _item$attributes7;
      return {
        ...base,
        meta: ((_item$attributes6 = item.attributes) === null || _item$attributes6 === void 0 ? void 0 : (_item$attributes6$con = _item$attributes6.container) === null || _item$attributes6$con === void 0 ? void 0 : _item$attributes6$con.title) || '',
        containerId: (_item$attributes7 = item.attributes) === null || _item$attributes7 === void 0 ? void 0 : _item$attributes7.containerId
      };
    }
    return base;
  });
  const getSectionDetailsByAccess = () => {
    if (hasAdvancedRoadmapsAccess) {
      return {
        scope: JiraScope.BoardProjectFilterPlans,
        recentHeading: messages.jira_recent_boards_projects_filters_plans_section_heading,
        searchHeading: messages.jira_boards_projects_filters_plans_section_heading
      };
    }
    if (hasSoftwareAccess) {
      return {
        scope: JiraScope.BoardProjectFilter,
        recentHeading: messages.jira_recent_boards_projects_filters_section_heading,
        searchHeading: messages.jira_boards_projects_filters_section_heading
      };
    }
    return {
      scope: JiraScope.ProjectFilter,
      recentHeading: messages.jira_recent_projects_filters_section_heading,
      searchHeading: messages.jira_projects_filters_section_heading
    };
  };
  const {
    formatMessage
  } = useIntl();
  const secondSectionDetails = getSectionDetailsByAccess();
  const enableNoIssueSection = (query, filters) => !filters.find(filter => {
    var _filter$accountIds, _filter$projectIds, _filter$binaryStatusC, _filter$issueLabels;
    return ((_filter$accountIds = filter['accountIds']) === null || _filter$accountIds === void 0 ? void 0 : _filter$accountIds.length) > 0 || ((_filter$projectIds = filter['projectIds']) === null || _filter$projectIds === void 0 ? void 0 : _filter$projectIds.length) > 0 || ((_filter$binaryStatusC = filter['binaryStatusCategories']) === null || _filter$binaryStatusC === void 0 ? void 0 : _filter$binaryStatusC.length) > 0 || ((_filter$issueLabels = filter['issueLabels']) === null || _filter$issueLabels === void 0 ? void 0 : _filter$issueLabels.length) > 0;
  });
  const resultRenderer = () => {
    return args => defaultResultRenderer(isConsistentIssueDetailsEnabled ? {
      ...args,
      isCollapsed: true
    } : args);
  };
  const sections = [{
    id: JiraScope.Issue,
    title: formatMessage(messages.jira_issues_section_heading),
    titleRenderer: sectionTitleRenderer(!!autocorrectEnabled),
    scope: JiraScope.Issue,
    resultMapper: mapJiraItems,
    showResultCount: true,
    resultLimit: ISSUE_LIST_LIMIT,
    viewAllLinkRenderer: viewAllLinkRendererIssues,
    resultRenderer: resultRenderer(),
    viewAllLinkGenerator,
    resultWrapper: isPreviewOnJiraSearchResultsEnabled ? HoverCardResultWrapper : undefined
  }, {
    id: secondSectionDetails.scope,
    title: formatMessage(secondSectionDetails.searchHeading),
    scope: secondSectionDetails.scope,
    resultMapper: mapJiraItems,
    isEnabled: enableNoIssueSection,
    resultLimit: hasAdvancedRoadmapsAccess ? OTHERS_LIST_LIMIT_ADVANCED_ROADMAPS_ACCESS : OTHERS_LIST_LIMIT_NO_ADVANCED_ROADMAPS_ACCESS
  }];
  const recentSectionTitleGenerator = section => section.id === JiraScope.Issue ? formatMessage(messages.jira_recently_viewed_issues_section_heading) : formatMessage(secondSectionDetails.recentHeading);
  return {
    recentSectionTitleGenerator,
    sections,
    mapJiraItems,
    getSectionDetailsByAccess
  };
};